import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import useOnce from '@hooks/useOnce';
import { intlShape } from 'react-intl';
import styled from 'styled-components';
import { ROUTES } from '@services/routes.service';
import { isValidDate } from '@utils/date';
import Dialog, { BUTTONS_TYPE } from '@components/Dialog';
import DateInput from '@components/DateInput';
import Input from '@components/Input';
import {
  MODAL_TYPE,
  TRACKING_ORIGIN,
  VALIDATION_RELIABLE,
} from '@commons/constants';

import {
  USER_VALIDATION,
  MIN_DOCUMENT_SIZE,
  MAX_DOCUMENT_SIZE,
} from './constants';
import { isValidDocument } from './utils';
import connect from './connect';
import messages from './messages';

const onCloseDefault = () => {};

const InputContainer = styled(Input)`
  margin-bottom: ${({ theme }) =>
    theme.cart.ageValidationReliable.marginBottom};
`;

function AgeValidationReliable({
  onAccept,
  onClose,
  open,
  intl: { formatMessage },
  fetchReliableAgeValidation,
  pushPage,
  showErrorToastRetry,
  trackModalOpened,
  trackModalClosed,
  trackAgeValidationFailed,
}) {
  useOnce(() => {
    trackModalOpened(MODAL_TYPE.AGE_VALIDATION_FORM);
  });

  const [date, setDate] = useState('');
  const [defaultErrorMessage, setDefaultErrorMessage] = useState(
    formatMessage(messages.errorDate),
  );
  const [loading, setLoading] = useState(false);
  const [identityCard, setIdentityCard] = useState('');
  const [showErrorIdentityCard, setShowErrorIdentityCard] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);

  const onCloseModal = useCallback(() => {
    setDate('');
    onClose();
    trackModalClosed(
      MODAL_TYPE.AGE_VALIDATION_FORM,
      TRACKING_ORIGIN.BUTTON.CLOSE,
      TRACKING_ORIGIN.BUTTON.TYPE,
    );
  }, [onClose, trackModalClosed]);

  const onAcceptModal = async () => {
    if (isValidDate(date) && isValidDocument(identityCard)) {
      setLoading(true);
      const status = await fetchReliableAgeValidation(identityCard, date);
      if (status) {
        setLoading(false);
        switch (status) {
          case USER_VALIDATION.VALID:
            trackModalClosed(
              MODAL_TYPE.AGE_VALIDATION_FORM,
              VALIDATION_RELIABLE.USER_VALID,
              TRACKING_ORIGIN.SUBMIT,
            );
            pushPage(ROUTES.CHECKOUT);
            break;
          case USER_VALIDATION.USER_NOT_OF_AGE:
            trackModalClosed(
              MODAL_TYPE.AGE_VALIDATION_FORM,
              VALIDATION_RELIABLE.USER_NOT_OF_AGE,
              TRACKING_ORIGIN.SUBMIT,
            );
            onAccept();
            break;
          case USER_VALIDATION.CARDID_BIRTHDAY_DONT_MATCH:
            trackAgeValidationFailed(
              VALIDATION_RELIABLE.CARDID_BIRTHDAY_DONT_MATCH,
            );
            setDefaultErrorMessage(formatMessage(messages.dateNotMatch));
            setShowErrorMessage(true);
            break;
          default:
            break;
        }
      } else {
        trackAgeValidationFailed(VALIDATION_RELIABLE.SERVICE_ERROR);
        showErrorToastRetry();
        onCloseModal();
      }
    }

    if (!isValidDate(date) && !isValidDocument(identityCard)) {
      setShowErrorMessage(true);
      setShowErrorIdentityCard(true);
      trackAgeValidationFailed(VALIDATION_RELIABLE.ID_CARD_FORMAT_ERROR);
      trackAgeValidationFailed(VALIDATION_RELIABLE.DATE_FORMAT_ERROR);
    } else if (!isValidDocument(identityCard)) {
      trackAgeValidationFailed(VALIDATION_RELIABLE.ID_CARD_FORMAT_ERROR);
      setShowErrorIdentityCard(true);
    } else {
      trackAgeValidationFailed(VALIDATION_RELIABLE.DATE_FORMAT_ERROR);
      setShowErrorMessage(true);
    }
  };

  const onChange = inputValue => {
    setIdentityCard(inputValue);
    if (
      inputValue.length < MIN_DOCUMENT_SIZE.AR ||
      inputValue.length > MAX_DOCUMENT_SIZE.AR
    ) {
      setShowErrorIdentityCard(true);
    } else {
      setShowErrorIdentityCard(false);
    }
  };

  const onCleanInput = useCallback(() => {
    setDate('');
    setShowErrorMessage(false);
  }, [setShowErrorMessage]);

  return (
    <Dialog
      onFirstOptionClick={onCloseModal}
      firstButtonText={formatMessage(messages.cancelButton)}
      onSecondOptionClick={onAcceptModal}
      secondButtonText={formatMessage(messages.acceptButton)}
      onClose={onCloseModal}
      open={open}
      title={formatMessage(messages.title)}
      text={formatMessage(messages.subtitle)}
      loading={loading}
      buttonsType={BUTTONS_TYPE.INLINE}
    >
      <InputContainer
        onChange={onChange}
        showErrorMessage={showErrorIdentityCard}
        errorMessage={formatMessage(messages.inputError)}
        label={formatMessage(messages.inputTitle)}
        elementId="age-validation-input"
        autoFocus
        value={identityCard}
        type="number"
      />
      <DateInput
        value={date}
        label={formatMessage(messages.pickerTitle)}
        onChange={setDate}
        onClean={onCleanInput}
        showErrorMessage={showErrorMessage}
        errorMessage={defaultErrorMessage}
      />
    </Dialog>
  );
}

AgeValidationReliable.propTypes = {
  onAccept: PropTypes.func,
  onClose: PropTypes.func,
  open: PropTypes.bool.isRequired,
  intl: intlShape,
  fetchReliableAgeValidation: PropTypes.func,
  pushPage: PropTypes.func,
  showErrorToastRetry: PropTypes.func,
  trackModalOpened: PropTypes.func,
  trackModalClosed: PropTypes.func,
  trackAgeValidationFailed: PropTypes.func,
};

AgeValidationReliable.defaultProps = {
  onClose: onCloseDefault,
};

export default connect(AgeValidationReliable);
